import SoftphoneLib from "../../../../softphone/softphone.es";
import config from "config";

const {
    constants: {
        API: { PRICING_UPDATE_URL, BATCHDIALER_BASE_URL, BATCHDIALER_POSTBACK_URL },
    },
} = config;

const REGISTRATION_TIMEOUT_MS = 20000;
function withTimeout(promise, ms, message) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(message)), ms);
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            }
        );
    });
}

class SoftphoneService {
    constructor() {
        this.softphone = null;
        this.isRegistered = false;
        this.registrationKey = null;
        this.registrationKeyPromise = null;
        this.callerId = null;
        this.micStream = null;
        this.onCallEnded = null;
        this.onCleanup = null;
        this._registeringPromise = null;
        this._micPermissionPromise = null;
    }

    isRegistered = this.softphone && this.isRegistered;

    async fetchRegistrationKey() {
        console.log('SoftphoneService fetching Temp API key');
        const res = await fetch(
            `${PRICING_UPDATE_URL}/ps/integrations/batch-dialer-api-key`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "x-auth-token": window.appAuthToken,
                },
            }
        );

        if (!res.ok) {
            throw new Error("Failed to fetch batch dialer API key");
        }

        const data = await res.json();
        return data?.apiKey;
    }

    async getRegistrationKey() {
        if (this.registrationKeyPromise) {
            return this.registrationKeyPromise;
        }

        this.registrationKeyPromise = this.fetchRegistrationKey()
            .finally(() => {
                this.registrationKeyPromise = null;
            });

        return this.registrationKeyPromise;
    }

    async prewarmRegistrationKey() {
        if (this.registrationKey != null) return this.registrationKey;
        try {
            this.registrationKey = await this.getRegistrationKey();
            console.log('[SoftphoneService] Registration key pre-warmed');
            return this.registrationKey;
        } catch (e) {
            console.error('[SoftphoneService] Failed to pre-warm registration key', e);
            return null;
        }
    }

    async ensureRegistered(agentInfo, callerId) {
        if (this.isRegistered) return;

        if (this._registeringPromise) {
            return this._registeringPromise;
        }

        this._registeringPromise = this.register(agentInfo, callerId)
            .finally(() => {
                this._registeringPromise = null;
            });

        return this._registeringPromise;
    }


    async prewarmRegistration(agentInfo, callerId) {
        try {
            await this.ensureRegistered(agentInfo, callerId);
            console.log('[SoftphoneService] Prewarm registration complete');
        } catch (e) {
            console.error('[SoftphoneService] Prewarm registration failed, will retry on click', e);
        }
    }

    async prewarmMicPermission(microphonePermissionHandler) {
        if (this.micStream) return this.micStream;
        if (this._micPermissionPromise) return this._micPermissionPromise;

        this._micPermissionPromise = microphonePermissionHandler.getPermission()
            .then(response => {
                if (!response.error) {
                    this.micStream = response.stream;
                }
                return response;
            })
            .catch(() => {})
            .finally(() => {
                this._micPermissionPromise = null;
            });

        return this._micPermissionPromise;
    }

    registerOnCleanup(eventHandler) {
        this.onCleanup = eventHandler;
    }

    registerOnCallEnded(eventHandler) {
        this.onCallEnded = eventHandler;
    }

    attachEvents() {
        if (!this.softphone) return;

        const stopMic = () => {
            if (this.micStream) {
                this.micStream.getTracks().forEach(track => {
                    if (track.kind === "audio") {
                        track.stop();
                    }
                });
                this.micStream = null;
                console.log("[SoftphoneService] Microphone stopped");
            }
        };

        this.softphone.on("register", () => {
            this.isRegistered = true;
            console.log('SoftphoneService after register state ' + this.isRegistered);
        });

        this.softphone.on("hangup", () => {
            console.log("[SoftphoneService] hangup");
            stopMic();
            this.cleanup();
            console.log('SoftphoneService after hangup state ' + this.isRegistered);

            this.onCallEnded?.();
            this.onCleanup?.();
        });

        this.softphone.on("hide", () => {
            console.log("[SoftphoneService] hide");
            if (this.softphone) {
                this.softphone.hangup();
            }
        });

        this.softphone.on("destroyed", () => {
            console.log("[SoftphoneService] destroyed");
            stopMic();
            this.cleanup();
            console.log('SoftphoneService after destroy state ' + this.isRegistered);
            this.onCleanup?.();
        });

        this.softphone.on("idle", () => {
            console.log("[SoftphoneService] idle");
            stopMic();
            this.destroy();
            this.cleanup();
            console.log('SoftphoneService after idle state ' + this.isRegistered);

            this.onCallEnded?.();
            this.onCleanup?.();
        });

        this.softphone.on("error", (err) => {
            console.error("Softphone error", err);
        });
    }

    cleanup() {
        this.softphone = null;
        this.isRegistered = false;
        this.registrationKey = null;
        this.callerId = null;
        this.micStream = null;
    }

    async register(agentInfo, callerId) {
        if (this.softphone) {
            this.softphone.destroy();
            this.softphone = null;
            this.isRegistered = false;
        }

        if (this.registrationKey == null) {
            this.registrationKey = await this.getRegistrationKey();
            console.log('SoftphoneService API key found');
        }
        this.callerId = callerId;

        if (!this.registrationKey) {
            throw new Error("Softphone registration key missing");
        }

        this.softphone = new SoftphoneLib.Softphone();
        this.softphone.enableAutoTests = false;

        this.attachEvents();

        try {
            await withTimeout(
                new Promise((resolve, reject) => {
                    this.softphone.on("register", () => {
                        this.isRegistered = true;
                        resolve();
                    });

            this.softphone.on("error", reject);

                    this.softphone.register(this.registrationKey, {
                        backendAPI: `${BATCHDIALER_BASE_URL}/api/`,
                        agent: agentInfo,
                        theme: "propstream",
                        minimized: false,
                        addonCallIntelligence: true,
                        addonCallIntelligenceOptions: {
                            trialBadge: false,
                            propertyLoad: false,
                        },
                    });
                }),
                REGISTRATION_TIMEOUT_MS,
                "Timed out registering with softphone service"
            );
        } catch (e) {
            this.destroy();
            throw e;
        }
    }

    async dial({ contact, propertyInfo, userId, accountId, marketingCampaignId, dialerCampaignId }) {
        if (!this.softphone || !this.isRegistered) {
            throw new Error("Softphone is not registered");
        }

        const rawNumber = contact?.phonenumber;
        const normalizedNumber = rawNumber
            ? String(rawNumber).replace(/[^\d+]/g, "")
            : rawNumber;

        let postbackUrl =
            `${BATCHDIALER_POSTBACK_URL}/api/webhook/batchdialer/log/add` +
            `?contactId=${contact.contactId}` +
            `&phoneNumber=${normalizedNumber}` +
            `&userId=${userId}`;

        if (marketingCampaignId) {
            postbackUrl += `&marketingCampaignId=${marketingCampaignId}`;
        }
        if (dialerCampaignId) {
            postbackUrl += `&dialerCampaignId=${dialerCampaignId}`;
        }

        this.softphone.setPostbackURL(postbackUrl);

        return this.softphone.dial({
            contact: { ...contact, phonenumber: normalizedNumber },
            callerID: this.callerId,
            propertyInfo,
        });
    }

    destroy() {
        if (this.softphone) {
            this.softphone.destroy();
            this.cleanup();
        }
    }
}

export default new SoftphoneService();